import { createAddFlowDiagnostic } from '#~/agent/components/bridge/add-flow-diagnostic.js';
import { Sel, STagNew } from '#~/stag/index.js';
import { Status } from '#~/device/components/status/index.js';
import { toStatus } from '#~/device/index.js';
import type { DeviceGovernorState } from '#~/livekit/bandwidth-governor.js';
import { Container } from '#~/video/components/viewer/index.js';
import { Button } from '@studio/design-system/button';
import { isErrored } from '@studio/models/device/stream';
import { dequal } from 'dequal/lite';
import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';
import { useLatestFn } from '@studio/react-utils/hooks';
import { useProducer, useStore } from '../store/hooks.js';
import {
  PREVIEW_FIRST_FRAME_MS,
  PREVIEW_LOST_PROGRESS_MS,
  PREVIEW_PENDING_COPY,
  unavailablePreviewCopy
} from '../store/preview-outcome.js';
import { requestPreviewRetry } from '../store/preview-prepare.js';
import type { PresentedFrame } from '#~/livekit/components/video/presented-frames.js';
import type { Props } from './types.js';

const isDocumentHidden = (): boolean =>
  typeof document !== 'undefined' && document.visibilityState === 'hidden';

/** Add owns frame availability; raw preparation has its separate 30-second deadline. */
export const Preview = ({ children, ...props }: Props): ReactElement => {
  const produce = useProducer();
  const diagnosticContext = useStore(
    (state) => ({
      actor: 'selected-bridge' as const,
      agentId: state.agentId,
      roomId: state.roomId,
      sourceId: state.source.selection?.id?.toString()
    }),
    dequal
  );
  const diagnosticRef = useRef<ReturnType<typeof createAddFlowDiagnostic> | undefined>(undefined);
  const deviceId = useStore(({ previews }) => previews.video?.deviceId);
  const videoError = useStore(({ previews }) => previews.video?.error);
  const status = useStore((µ) => {
    const {
      external,
      previews: { video }
    } = µ;

    const { transients } = external;
    const transient = video?.deviceId && !video.error ? transients[video.deviceId] : undefined;

    return transient ? toStatus(transient) : undefined;
  }, dequal);
  const {
    previewAvailability,
    previewCopy,
    previewPending,
    previewReady,
    previewGeneration,
    protocol
  } = useStore(
    (µ) => ({
      previewAvailability: µ.previewAvailability,
      previewCopy: µ.previewCopy,
      previewPending: µ.previewPending,
      previewReady: µ.previewReady,
      previewGeneration: µ.previewGeneration,
      protocol:
        µ.candidate.video && 'protocol' in µ.candidate.video
          ? µ.candidate.video.protocol
          : undefined
    }),
    dequal
  );

  // Consume the existing governor subscription instead of competing with deliberate pauses.
  const [governorState, setGovernorState] = useState<DeviceGovernorState>('normal');
  const onGovernorState = useCallback((next: DeviceGovernorState): void => {
    setGovernorState(next);
  }, []);
  const isPaused = governorState === 'paused-auto' || governorState === 'paused-manual';

  // Opening in a hidden document must not spend the frame budget.
  const [isHidden, setIsHidden] = useState(isDocumentHidden);
  const [isDismissed, setIsDismissed] = useState(false);

  // Retained old callbacks cannot satisfy another generation/device or a paused observer.
  const canObserve =
    !previewPending && previewReady !== false && !videoError && !isHidden && !isPaused;
  const identityRef = useRef({ previewGeneration, deviceId, canObserve });
  identityRef.current = { previewGeneration, deviceId, canObserve };
  useEffect(
    () => () => {
      identityRef.current.canObserve = false;
    },
    []
  );

  // Avoid publishing identical store state on every decoded frame.
  const publishedRef = useRef({ previewAvailability, previewCopy });
  publishedRef.current = { previewAvailability, previewCopy };

  const timerRef = useRef<number | undefined>(undefined);
  const sawFrameRef = useRef(false);

  const clearTimer = useLatestFn(() => {
    if (timerRef.current !== undefined) {
      window.clearTimeout(timerRef.current);
      timerRef.current = undefined;
    }
  });

  // Frame callbacks can arm timers after the deadline effect returns early.
  // Unmount must cancel those timers too, preventing stale state and diagnostics.
  useEffect(
    () => () => {
      clearTimer();
    },
    [clearTimer]
  );

  const markUnavailable = useLatestFn((category: 'no-frame' | 'unknown' = 'no-frame') => {
    timerRef.current = undefined;
    diagnosticRef.current?.report({
      operation: 'preview-receive',
      category,
      retry: 'retry',
      deviceId
    });
    produce((draft) => {
      draft.previewAvailability = 'unavailable';
      if (!draft.previewCopy) {
        draft.previewCopy = unavailablePreviewCopy(protocol);
      }
    });
  });

  useEffect(() => {
    const onVisibility = (): void => setIsHidden(isDocumentHidden());

    document.addEventListener('visibilitychange', onVisibility);
    onVisibility();

    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  // A new generation or a replaced device is a new observation.
  useEffect(() => {
    sawFrameRef.current = false;
    setIsDismissed(false);
  }, [previewGeneration, deviceId]);

  // A dismissed pending overlay must resurface a later failure with Retry enabled.
  useEffect(() => {
    if (!previewPending) {
      setIsDismissed(false);
    }
  }, [previewPending]);

  useEffect(() => {
    diagnosticRef.current = createAddFlowDiagnostic(diagnosticContext);
  }, [previewGeneration, deviceId, diagnosticContext]);

  useEffect(() => {
    if (!videoError) {
      return;
    }

    clearTimer();
    produce((draft) => {
      draft.previewAvailability = 'unavailable';
    });
  }, [videoError, previewGeneration, produce, clearTimer]);

  useEffect(() => {
    clearTimer();

    // Frame timing starts only after this attempt has completed raw preparation.
    if (
      previewPending ||
      previewReady === false ||
      !deviceId ||
      videoError ||
      isHidden ||
      isPaused
    ) {
      return undefined;
    }

    if (publishedRef.current.previewAvailability === 'unavailable') {
      return undefined;
    }

    timerRef.current = window.setTimeout(
      markUnavailable,
      sawFrameRef.current ? PREVIEW_LOST_PROGRESS_MS : PREVIEW_FIRST_FRAME_MS
    );

    return clearTimer;
  }, [
    clearTimer,
    deviceId,
    isHidden,
    isPaused,
    markUnavailable,
    previewGeneration,
    previewPending,
    previewReady,
    videoError
  ]);

  useEffect(() => {
    if (!canObserve || !deviceId || !isErrored(status?.video?.status)) {
      return;
    }

    // Explicit failure needs no frame deadline; later frames can supersede an unchanged status echo.
    clearTimer();
    markUnavailable('unknown');
  }, [canObserve, clearTimer, deviceId, markUnavailable, previewGeneration, status?.video?.status]);

  const onPresentedFrame = useCallback(
    (_frame: PresentedFrame): void => {
      const live = identityRef.current;

      // Compare captured identity before publishing or rearming a timer.
      if (
        !live.canObserve ||
        live.previewGeneration !== previewGeneration ||
        live.deviceId !== deviceId
      ) {
        return;
      }

      if (previewPending || previewReady === false || videoError || isHidden || isPaused) {
        return;
      }

      sawFrameRef.current = true;
      clearTimer();
      timerRef.current = window.setTimeout(markUnavailable, PREVIEW_LOST_PROGRESS_MS);

      const published = publishedRef.current;
      if (published.previewAvailability === 'receiving' && published.previewCopy === undefined) {
        return;
      }

      produce((draft) => {
        draft.previewAvailability = 'receiving';
        draft.previewCopy = undefined;
      });
    },
    [
      clearTimer,
      deviceId,
      isHidden,
      isPaused,
      markUnavailable,
      previewGeneration,
      previewPending,
      previewReady,
      videoError,
      produce
    ]
  );

  const retry = (): void => {
    if (previewPending) {
      return;
    }

    requestPreviewRetry(produce);
  };

  // Manual RTSP failures need actions even when Container has no device to render.
  const showOutcome = previewAvailability === 'unavailable' && !!previewCopy && !isDismissed;

  return (
    <div className='relative'>
      <Container
        deviceId={deviceId}
        onGovernorState={onGovernorState}
        onPresentedFrame={
          previewReady === false || previewPending || videoError ? undefined : onPresentedFrame
        }
        {...props}
      >
        {/* Overlay already states the condition; a Connecting pill in the same box contradicts it and collides. */}
        {!!deviceId && !!status && !showOutcome && (
          <Status deviceId={deviceId} hideDeviceStatus status={status} />
        )}
      </Container>
      {children}
      {showOutcome ? (
        <div className='bg-black/70 absolute inset-0 z-10 flex flex-col items-center justify-center gap-12 p-16 text-center'>
          <p className='text-sm text-white'>{previewCopy}</p>
          {previewPending && <p className='text-white/70 text-xs'>{PREVIEW_PENDING_COPY}</p>}
          <div className='flex items-center gap-8'>
            <Button
              {...STagNew(Sel.AddVideoDeviceDiscovery, 'preview_retry')}
              disabled={previewPending}
              onClick={retry}
              size='sm'
              variant='secondary'
            >
              Retry preview
            </Button>
            {previewPending && (
              <Button
                {...STagNew(Sel.AddVideoDeviceDiscovery, 'preview_dismiss')}
                onClick={() => setIsDismissed(true)}
                size='sm'
                variant='tertiary'
              >
                Dismiss
              </Button>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};
